import { jsx as _jsx } from "react/jsx-runtime";
import { ThunderboltOutlined } from "@ant-design/icons";
import { runInAction } from "mobx";
import { Button, Card, Skeleton, Tag, Tooltip, message } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
// Components
import { DataTable } from "@/components/DataTable";
// Hooks
import { getRootStore } from "@/stores/RootStore";
// Types and Utils
import { CHARGE_LEVEL_LABELS, OrderStatus, } from "@/types/order/order.common";
import { formatDateAndTime } from "@/utils/dateAndTimeFormat";
import { ORDER_STATUS_LABELS, getOrderStatusColor } from "@/utils/orderHelpers";
// Services
import { routeCalculationService } from "@/services/RouteCalculationService";
import { RoutePointMapper } from "@/adapters/RoutePoint.mapper";
import { Base2RoutePointCreateData } from "@/adapters/Base2RoutePointTransferData.mapper";
import { OrderToRoutePointCreateData } from "@/adapters/OrderToRoutePointTransferData.mapper";
// Styles
import styles from "@/styles/QuickOrdersPage.module.less";
const QUICK_ORDER_FILTERS = {
    status: [OrderStatus.NEW, OrderStatus.CONFIRMED],
    // WP-DSP-FIX-03: заказ из живого маршрута сюда не попадает — «В маршрут» не создаст второй маршрут
    withoutRoute: true,
};
const QuickOrdersPage = observer(() => {
    const { orderStore, basesStore } = getRootStore();
    const { dataTable, loading } = orderStore;
    const navigate = useNavigate();
    const [calculating, setCalculating] = useState(null);
    useEffect(() => {
        runInAction(() => {
            dataTable.reset();
        });
        dataTable.setFilters(QUICK_ORDER_FILTERS);
        dataTable.loadData();
        basesStore.dataTable.loadData();
        return () => {
            dataTable.setFilters({});
        };
    }, []);
    const handleCreateRoute = async (order) => {
        try {
            setCalculating(order.id);
            const bases = basesStore.dataTable.data;
            if (!bases || bases.length === 0) {
                message.error("Нет доступных баз для построения маршрута");
                return;
            }
            // Calculate travel time from each base to the order
            const baseMapper = new Base2RoutePointCreateData();
            const orderMapper = new OrderToRoutePointCreateData();
            const promises = bases.map(async (base) => {
                try {
                    const basePoint = RoutePointMapper.toCreateDto(baseMapper.adapt(base));
                    const orderPoint = RoutePointMapper.toCreateDto(orderMapper.adapt(order));
                    const calculatedPoints = await routeCalculationService.calculateRoutePointTimes(dayjs().toISOString(), [basePoint, orderPoint]);
                    const baseResult = calculatedPoints[0];
                    const orderResult = calculatedPoints[1];
                    if (baseResult.plannedDepartureTime && orderResult.plannedArrivalTime) {
                        const start = dayjs(baseResult.plannedDepartureTime);
                        const end = dayjs(orderResult.plannedArrivalTime);
                        const duration = end.diff(start, 'seconds');
                        return { base, duration };
                    }
                    return null;
                }
                catch (e) {
                    console.error("[QuickOrders] Calculation failed for base", base.id, e);
                    return null;
                }
            });
            const results = await Promise.all(promises);
            const validResults = results.filter((r) => r !== null);
            if (validResults.length === 0) {
                message.error("Не удалось рассчитать маршруты ни от одной базы");
                return;
            }
            // Sort by duration ascending
            validResults.sort((a, b) => a.duration - b.duration);
            const bestBase = validResults[0].base;
            const bestDurationSeconds = validResults[0].duration;
            // Calculate start time - but never in the past
            const now = dayjs();
            const orderTime = dayjs(order.scheduledDate);
            let calculatedStartTime = orderTime.subtract(bestDurationSeconds, 'seconds');
            // If calculated start time is in the past, use current time
            if (calculatedStartTime.isBefore(now)) {
                calculatedStartTime = now;
            }
            // Navigate to route creation - pass only IDs to avoid serialization issues
            navigate("/routes/new", {
                state: {
                    quickOrder: {
                        baseId: bestBase.id,
                        orderId: order.id,
                        plannedStartTime: calculatedStartTime.toISOString()
                    }
                }
            });
        }
        catch (error) {
            console.error(error);
            message.error("Произошла ошибка при поиске оптимальной базы");
        }
        finally {
            setCalculating(null);
        }
    };
    const columns = [
        {
            title: "Номер заказа",
            dataIndex: "orderNumber",
            key: "orderNumber",
            fixed: "left",
            width: 150,
            render: (text, record) => (_jsx(Button, { type: "link", className: styles.orderNumberLink, onClick: () => navigate(`/orders/${record.id}`), children: text })),
        },
        {
            title: "Статус",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status) => (_jsx(Tag, { color: getOrderStatusColor(status), children: ORDER_STATUS_LABELS[status] })),
        },
        {
            title: "Адрес",
            key: "addressInfo",
            width: 250,
            ellipsis: {
                showTitle: false,
            },
            render: (_, record) => record.addressInfo.getAddress() || "Адрес не указан",
        },
        {
            title: "Дата/Время",
            key: "scheduledDate",
            dataIndex: "scheduledDate",
            width: 160,
            render: (_, record) => (_jsx("div", { children: _jsx("div", { children: formatDateAndTime(record.scheduledDate) }) })),
            sorter: true,
        },
        {
            title: "Тип зарядки",
            key: "chargeLevel",
            width: 120,
            render: (_, record) => (_jsx("div", { children: CHARGE_LEVEL_LABELS[record.chargeLevel] })),
        },
        {
            title: "Действия",
            key: "actions",
            fixed: "right",
            width: 180,
            render: (_, record) => (_jsx(Tooltip, { title: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u043C\u0430\u0440\u0448\u0440\u0443\u0442 \u043E\u0442 \u0431\u043B\u0438\u0436\u0430\u0439\u0448\u0435\u0439 \u0431\u0430\u0437\u044B", children: _jsx(Button, { type: "primary", icon: _jsx(ThunderboltOutlined, {}), loading: calculating === record.id, onClick: () => handleCreateRoute(record), children: "\u0412 \u043C\u0430\u0440\u0448\u0440\u0443\u0442" }) })),
        },
    ];
    const dataTableConfig = {
        header: {
            title: "Быстрые заказы",
        },
        columns: columns,
        rowKey: "id",
        scroll: { x: 1400 },
    };
    if (loading) {
        return (_jsx(Card, { children: _jsx(Skeleton, { active: true, paragraph: { rows: 8 } }) }));
    }
    return _jsx(DataTable, { store: dataTable, config: dataTableConfig });
});
QuickOrdersPage.displayName = "QuickOrdersPage";
export default QuickOrdersPage;
